import { useState, useEffect, useCallback, useRef } from 'react';

type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
type Position = { x: number; y: number };

const GRID_SIZE = 20;
const CELL_SIZE = 18;
const INITIAL_SPEED = 150;

interface SnakeProps {
  onGameEnd: (winner: string | null) => void;
  sendGameState: (state: any) => void;
  isHost: boolean;
  gameId: string;
  isOffline?: boolean;
}

function randomPos(): Position {
  return {
    x: Math.floor(Math.random() * GRID_SIZE),
    y: Math.floor(Math.random() * GRID_SIZE),
  };
}

export default function Snake({ onGameEnd, sendGameState, isHost, isOffline }: SnakeProps) {
  const [snake, setSnake] = useState<Position[]>([{ x: 5, y: 10 }]);
  const [food, setFood] = useState<Position>({ x: 15, y: 10 });
  const [, setDirection] = useState<Direction>('RIGHT');
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  // Opponent state
  const [opponentSnake, setOpponentSnake] = useState<Position[]>([]);
  const [opponentScore, setOpponentScore] = useState(0);
  const [opponentDead, setOpponentDead] = useState(false);

  // Refs for use inside game loop callbacks
  const directionRef = useRef<Direction>('RIGHT');
  const snakeRef = useRef<Position[]>([{ x: 5, y: 10 }]);
  const foodRef = useRef<Position>({ x: 15, y: 10 });
  const scoreRef = useRef(0);
  const gameOverRef = useRef(false);
  const opponentSnakeRef = useRef<Position[]>([]);

  const syncDirection = (d: Direction) => {
    directionRef.current = d;
    setDirection(d);
  };

  // Listen for opponent game state
  useEffect(() => {
    const handleGameState = (e: CustomEvent) => {
      const state = e.detail;
      if (state.type === 'snake') {
        opponentSnakeRef.current = state.positions ?? [];
        setOpponentSnake(state.positions ?? []);
        setOpponentScore(state.score ?? 0);
        if (state.isDead && !gameOverRef.current) {
          setOpponentDead(true);
          // Opponent died — we win (if we're still alive)
          if (!gameOverRef.current) {
            gameOverRef.current = true;
            setGameOver(true);
            setTimeout(() => onGameEnd('you'), 800);
          }
        }
        // Host syncs food to guest
        if (!isHost && state.food) {
          foodRef.current = state.food;
          setFood(state.food);
        }
      }
    };

    window.addEventListener('game_state', handleGameState as EventListener);
    return () => window.removeEventListener('game_state', handleGameState as EventListener);
  }, [isHost, onGameEnd]);

  // Keyboard controls — prevent opposite direction
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (gameOverRef.current) return;
      const cur = directionRef.current;
      switch (e.key) {
        case 'ArrowUp':    if (cur !== 'DOWN')  syncDirection('UP');    e.preventDefault(); break;
        case 'ArrowDown':  if (cur !== 'UP')    syncDirection('DOWN');  e.preventDefault(); break;
        case 'ArrowLeft':  if (cur !== 'RIGHT') syncDirection('LEFT');  e.preventDefault(); break;
        case 'ArrowRight': if (cur !== 'LEFT')  syncDirection('RIGHT'); e.preventDefault(); break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  const generateFood = useCallback((): Position => {
    let pos: Position;
    do {
      pos = randomPos();
    } while (snakeRef.current.some(s => s.x === pos.x && s.y === pos.y));
    return pos;
  }, []);

  const moveSnake = useCallback(() => {
    if (gameOverRef.current) return;

    setSnake(prevSnake => {
      const head = { ...prevSnake[0] };

      switch (directionRef.current) {
        case 'UP':    head.y -= 1; break;
        case 'DOWN':  head.y += 1; break;
        case 'LEFT':  head.x -= 1; break;
        case 'RIGHT': head.x += 1; break;
      }

      // Wall collision
      if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
        gameOverRef.current = true;
        setGameOver(true);
        sendGameState({ type: 'snake', positions: prevSnake, score: scoreRef.current, isDead: true });
        setTimeout(() => onGameEnd('opponent'), 800);
        return prevSnake;
      }

      // Self collision
      if (prevSnake.slice(1).some(s => s.x === head.x && s.y === head.y)) {
        gameOverRef.current = true;
        setGameOver(true);
        sendGameState({ type: 'snake', positions: prevSnake, score: scoreRef.current, isDead: true });
        setTimeout(() => onGameEnd('opponent'), 800);
        return prevSnake;
      }

      // Collision with opponent snake (online only)
      if (!isOffline && opponentSnakeRef.current.some(s => s.x === head.x && s.y === head.y)) {
        gameOverRef.current = true;
        setGameOver(true);
        sendGameState({ type: 'snake', positions: prevSnake, score: scoreRef.current, isDead: true });
        setTimeout(() => onGameEnd('opponent'), 800);
        return prevSnake;
      }

      const newSnake = [head, ...prevSnake];
      const ateFood = head.x === foodRef.current.x && head.y === foodRef.current.y;

      if (ateFood) {
        scoreRef.current += 1;
        setScore(scoreRef.current);
        const newFood = generateFood();
        foodRef.current = newFood;
        setFood(newFood);
        // Host is authoritative for food position
        if (isHost) {
          sendGameState({ type: 'snake', positions: newSnake, score: scoreRef.current, food: newFood });
        } else {
          sendGameState({ type: 'snake', positions: newSnake, score: scoreRef.current });
        }
      } else {
        newSnake.pop();
        sendGameState({ type: 'snake', positions: newSnake, score: scoreRef.current, food: isHost ? foodRef.current : undefined });
      }

      snakeRef.current = newSnake;
      return newSnake;
    });
  }, [generateFood, isHost, isOffline, onGameEnd, sendGameState]);

  useEffect(() => {
    const interval = setInterval(moveSnake, INITIAL_SPEED);
    return () => clearInterval(interval);
  }, [moveSnake]);

  const getCellState = (x: number, y: number) => {
    if (snake[0]?.x === x && snake[0]?.y === y) return 'head';
    if (snake.slice(1).some(s => s.x === x && s.y === y)) return 'snake';
    if (!isOffline && opponentSnake.some(s => s.x === x && s.y === y)) return 'opponent';
    if (food.x === x && food.y === y) return 'food';
    return 'empty';
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Scoreboard */}
      <div className="flex gap-8 text-center">
        <div>
          <div className="text-xs text-gray-300 mb-1">You</div>
          <div className="text-2xl font-bold text-green-400">{score}</div>
        </div>
        {!isOffline && (
          <div>
            <div className="text-xs text-gray-300 mb-1">Opponent</div>
            <div className={`text-2xl font-bold ${opponentDead ? 'text-red-400' : 'text-purple-400'}`}>
              {opponentScore}
            </div>
          </div>
        )}
      </div>

      {/* Grid */}
      <div
        className="bg-black/30 backdrop-blur-sm rounded-lg p-3 border border-white/10"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${GRID_SIZE}, ${CELL_SIZE}px)`,
          gap: '1px',
        }}
      >
        {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, i) => {
          const x = i % GRID_SIZE;
          const y = Math.floor(i / GRID_SIZE);
          const cell = getCellState(x, y);

          return (
            <div
              key={i}
              style={{ width: CELL_SIZE, height: CELL_SIZE }}
              className={`rounded-sm transition-colors ${
                cell === 'head'     ? 'bg-green-400' :
                cell === 'snake'    ? 'bg-green-600' :
                cell === 'opponent' ? 'bg-purple-500' :
                cell === 'food'     ? 'bg-red-500' :
                'bg-white/5'
              }`}
            />
          );
        })}
      </div>

      {/* Status */}
      <div className="text-sm text-gray-300">
        {gameOver
          ? <span className="text-red-400 font-bold">Game Over!</span>
          : 'Arrow keys to move'}
      </div>

      {!isOffline && (
        <div className="text-xs text-gray-300">
          Green = you · Purple = opponent
        </div>
      )}
    </div>
  );
}
